import {
  EGroupActionType,
  IGroupActionContent,
  IGroupActionContentBase,
  TGroupActionsLocale,
} from "@rentiohq/shared-frontend/dist/types/groupAction.types";
import { getLocalizedText } from "@rentiohq/shared-frontend/dist/utils/i18n/i18n.utils";
import { DisplayText } from "@rentiohq/web-shared/dist/components";
import React from "react";
import { getType } from "../GroupActions.util";
import AddGroupActionContentWithEditorSection from "./AddGroupActionContentWithEditorSection";
import AddGroupActionContentWithTextfieldSection, {
  type IAttachmentData,
} from "./AddGroupActionContentWithTextfieldSection";

export type { IAttachmentData };

export interface IAddGroupActionContentProps {
  type: EGroupActionType;
  initialContents: IGroupActionContent;
  onChange: (content: Record<TGroupActionsLocale, string>) => void;
  onChangeError: (error?: string) => void;
  onChangeTitle?: (key: TGroupActionsLocale, value?: string) => void;
  onChangeSubject?: (key: TGroupActionsLocale, value?: string) => void;
  onChangeAttachments?: (
    attachments: Record<TGroupActionsLocale, IAttachmentData | undefined>,
  ) => void;
}

const AddGroupActionContent = (props: IAddGroupActionContentProps) => {
  const {
    type,
    initialContents,
    onChange,
    onChangeError,
    onChangeTitle,
    onChangeSubject,
    onChangeAttachments,
  } = props;

  const [contents, setContents] =
    React.useState<IGroupActionContent>(initialContents);
  const [attachments, setAttachments] = React.useState<
    Record<TGroupActionsLocale, IAttachmentData | undefined>
  >({ nl_BE: undefined, fr_FR: undefined, en_UK: undefined });

  React.useEffect(() => {
    onChange({
      nl_BE: contents.nl_BE.value,
      fr_FR: contents.fr_FR.value,
      en_UK: contents.en_UK.value,
    });
  }, [contents]);

  React.useEffect(() => {
    onChangeAttachments?.(attachments);
  }, [attachments]);

  const handleContentChange = (locale: TGroupActionsLocale, value: string) => {
    setContents(prev => ({ ...prev, [locale]: { ...prev[locale], value } }));
  };

  const handleAttachmentChange = (
    locale: TGroupActionsLocale,
    attachment?: IAttachmentData,
  ) => {
    setAttachments(prev => ({ ...prev, [locale]: attachment }));
  };

  const renderContent = (
    [key, content]: [string, IGroupActionContentBase],
    index: number,
  ) => {
    const locale = key as TGroupActionsLocale;
    const commonProps = {
      key,
      locale,
      index,
      type,
      initialContent: content,
      onChange: (updated: IGroupActionContentBase) =>
        handleContentChange(locale, updated.value),
      onChangeTitle,
      onChangeSubject,
      onChangeError,
    };

    if ([EGroupActionType.Email, EGroupActionType.Sms].includes(type)) {
      return <AddGroupActionContentWithEditorSection {...commonProps} />;
    }

    return (
      <AddGroupActionContentWithTextfieldSection
        {...commonProps}
        onChangeAttachment={handleAttachmentChange}
      />
    );
  };

  return (
    <>
      <DisplayText size="medium">
        {getLocalizedText("group_actions.modal.content.title", {
          type: getType(type),
        })}
      </DisplayText>

      {Object.entries(contents).map(renderContent)}
    </>
  );
};

// eslint-disable-next-line import/no-default-export
export default AddGroupActionContent;
